import React, { useState, useEffect, useCallback } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ReferenceLine, ReferenceArea, ResponsiveContainer,
} from 'recharts';
import AccountDropdown from './AccountDropdown';
import { apiFetch } from './api';
import { fmtDollar, fmtPct, fmtMillions } from './intelligence/shared';

function fmtRange(low, high) {
  return `${fmtDollar(low)} – ${fmtDollar(high)}`;
}

function fmtTotalRange(low, high) {
  return `${fmtMillions(low)} – ${fmtMillions(high)}`;
}

function isTextSelected() {
  const sel = window.getSelection();
  return sel && sel.toString().length > 0;
}
import './InitiativesPage.css';

// ── Tab config ────────────────────────────────────────────────────────────────

const TABS = [
  { key: 'tracked',     label: 'Tracked Initiatives' },
  { key: 'recommended', label: 'Recommended Initiatives'  },
];

const VALID_KEYS = TABS.map(t => t.key);
function tabFromURL() {
  const p = new URLSearchParams(window.location.search).get('tab');
  return VALID_KEYS.includes(p) ? p : 'tracked';
}

// ── Tracked Initiatives ───────────────────────────────────────────────────────

function InitiativeChart({ initiative }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    apiFetch(`/api/initiatives/${initiative.id}/chart-data`)
      .then(r => r.json())
      .then(setData)
      .catch(() => setData([]));
  }, [initiative.id]);

  if (!data) return <div style={{ height: 160, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><span className="text-muted" style={{ fontSize: '0.8rem' }}>Loading…</span></div>;

  const startMonth = initiative.startDate.slice(0, 7);
  const endMonth   = initiative.endDate.slice(0, 7);
  const todayMonth = new Date().toISOString().slice(0, 7);
  const shadedEnd  = endMonth < todayMonth ? endMonth : todayMonth;

  return (
    <ResponsiveContainer width="100%" height={160}>
      <LineChart data={data} margin={{ top: 12, right: 16, left: 4, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e8eef4" />
        <XAxis dataKey="month" tick={{ fontSize: 10 }} tickLine={false} />
        <YAxis
          tick={{ fontSize: 10 }}
          tickFormatter={v => `$${v}`}
          domain={['auto', 'auto']}
          width={52}
        />
        <Tooltip formatter={v => [fmtDollar(v), 'PMPM']} />
        <ReferenceLine y={initiative.initialPmpm} stroke="#9aa5b1" strokeDasharray="4 3" strokeWidth={1.5} />
        <ReferenceLine y={initiative.targetPmpm}  stroke="#198754" strokeDasharray="4 3" strokeWidth={1.5} />
        <ReferenceArea x1={startMonth} x2={shadedEnd} fill="#9aa5b1" fillOpacity={0.15} stroke="none" />
        <Line
          type="monotone"
          dataKey="pmpm"
          stroke="#003c71"
          strokeWidth={2}
          dot={false}
          activeDot={{ r: 4 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

function TrackedTab({ onSwitchTab }) {
  const [initiatives, setInitiatives] = useState(null);

  const load = useCallback(() => {
    apiFetch('/api/initiatives').then(r => r.json()).then(setInitiatives).catch(() => setInitiatives([]));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = (id) => {
    apiFetch(`/api/initiatives/${id}`, { method: 'DELETE' }).then(load).catch(() => {});
  };

  if (!initiatives) return <div className="ini-loading">Loading…</div>;

  if (initiatives.length === 0) {
    return (
      <div className="ini-empty">
        <p>No initiatives are being tracked yet.</p>
        <button className="btn btn-primary btn-sm" onClick={() => onSwitchTab('recommended')}>
          Browse Recommended Initiatives
        </button>
      </div>
    );
  }

  return (
    <div className="ini-cards-grid">
      {initiatives.map(iv => (
        <div key={iv.id} className="card ini-initiative-card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <span>{iv.initiativeName}</span>
            <button
              className="btn-close btn-close-white"
              style={{ fontSize: '0.65rem' }}
              title="Remove initiative"
              onClick={() => handleDelete(iv.id)}
            />
          </div>
          <div className="card-body">
            <div className="ini-chart-meta">
              <span className="ini-meta-pop">{iv.populationName}</span>
              <span className="ini-meta-dates">{iv.startDate} – {iv.endDate}</span>
              <span className="ini-meta-assignee">{iv.enlaceManaged ? '★ ' : ''}{iv.assignee}</span>
            </div>
            <InitiativeChart initiative={iv} />
            <div className="ini-chart-legend">
              <span className="ini-legend-item ini-legend-baseline">Baseline ({fmtDollar(iv.initialPmpm)})</span>
              <span className="ini-legend-item ini-legend-target">Target ({fmtDollar(iv.targetPmpm)})</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Recommended Initiatives ───────────────────────────────────────────────────

function InitiativesTable({ categories, onSelectInitiative }) {
  if (!categories) return <div className="ini-loading">Loading…</div>;

  const grandTotal = categories.reduce((sum, c) => sum + c.subtotalPmpmMid, 0);

  return (
    <div className="ini-initiatives-wrap">
      {categories.map(cat => (
        <div key={cat.category} className="ini-initiatives-category">
          <div className="ini-category-header">
            <span className="ini-category-name">{cat.category}</span>
            <span className="ini-category-meta">{cat.leverCount} levers · {fmtDollar(cat.subtotalPmpmMid)} subtotal PMPM mid</span>
          </div>
          <table className="dash-table ini-initiatives-table w-100">
            <thead>
              <tr>
                <th>Initiative</th>
                <th>Module</th>
                <th className="text-end">Opportunity Range (PMPM)</th>
                <th className="text-end">Realized Range (PMPM)</th>
                <th className="text-end">Realized Range (Total)</th>
                <th className="text-end">Realized Range (% of Total Spend)</th>
              </tr>
            </thead>
            <tbody>
              {cat.initiatives.map(m => (
                <tr key={m.name} className="ini-initiative-row" onClick={() => { if (!isTextSelected()) onSelectInitiative(m); }} title={m.notes}>
                  <td className="ini-initiative-name">{m.name}</td>
                  <td style={{ color: 'var(--eh-text-mid)' }}>{m.module}</td>
                  <td className="text-end">{fmtRange(m.pmpmLow, m.pmpmHigh)}</td>
                  <td className="text-end">{fmtRange(m.realizedLow, m.realizedHigh)}</td>
                  <td className="text-end">{fmtTotalRange(m.realizedTotalLow, m.realizedTotalHigh)}</td>
                  <td className="text-end fw-semibold">{fmtPct(m.pctOfOpportunity, 1)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
      <div className="ini-grand-total">
        <span>Grand Total — All Buckets</span>
        <span>{fmtDollar(grandTotal)} PMPM mid</span>
      </div>
    </div>
  );
}

function CreationForm({ metric, populations, onSave, onCancel }) {
  const [selectedPop, setSelectedPop] = useState(null);
  const [targetPmpm, setTargetPmpm]   = useState('');
  const [savingsPct, setSavingsPct]   = useState('');
  const [startDate, setStartDate]     = useState('');
  const [endDate, setEndDate]         = useState('');
  const [assignee, setAssignee]       = useState('');
  const [enlaceManaged, setEnlaceManaged] = useState(false);
  const [saving, setSaving]           = useState(false);

  const initialPmpm = selectedPop ? selectedPop.pmpm : null;

  const handlePopSelect = (pop) => {
    setSelectedPop(pop);
    setTargetPmpm(pop.pmpm.toFixed(2));
    setSavingsPct('0.00');
  };

  const handleTargetChange = (val) => {
    setTargetPmpm(val);
    if (initialPmpm && parseFloat(val) > 0) {
      setSavingsPct((((initialPmpm - parseFloat(val)) / initialPmpm) * 100).toFixed(2));
    }
  };

  const handlePctChange = (val) => {
    setSavingsPct(val);
    if (initialPmpm && parseFloat(val) >= 0) {
      setTargetPmpm((initialPmpm * (1 - parseFloat(val) / 100)).toFixed(2));
    }
  };

  const handleEnlaceToggle = (checked) => {
    setEnlaceManaged(checked);
    if (checked) setAssignee('Enlace Health');
    else if (assignee === 'Enlace Health') setAssignee('');
  };

  const canSave = selectedPop && targetPmpm && startDate && endDate && assignee;

  const handleSave = async () => {
    setSaving(true);
    try {
      const resp = await apiFetch('/api/initiatives', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          initiativeName: metric.name,
          populationName: selectedPop.name,
          startDate,
          endDate,
          assignee,
          enlaceManaged,
          initialPmpm: initialPmpm,
          targetPmpm: parseFloat(targetPmpm),
        }),
      });
      if (resp.ok) onSave();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ini-creation-form">
      <div className="ini-form-header">
        <button className="btn btn-link btn-sm ini-back-btn p-0" onClick={onCancel}>← Back to initiatives</button>
        <h5 className="mb-0">{metric.name}</h5>
        <span className="badge bg-secondary" style={{ fontSize: '0.72rem' }}>{metric.module}</span>
      </div>

      <div className="ini-form-body">
        {/* Section 1: Population */}
        <div className="ini-form-section">
          <div className="ini-section-heading">1. Select a Population</div>
          {populations.map(group => (
            <div key={group.group} className="ini-pop-group">
              <div className="ini-pop-group-label">{group.group}</div>
              <div className="ini-pop-options">
                {group.populations.map(pop => (
                  <button
                    key={pop.name}
                    className={`ini-pop-option${selectedPop?.name === pop.name ? ' selected' : ''}`}
                    onClick={() => handlePopSelect(pop)}
                  >
                    <span className="ini-pop-name">{pop.name}</span>
                    <span className="ini-pop-pmpm">{fmtDollar(pop.pmpm)} PMPM</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Section 2: Target PMPM */}
        <div className="ini-form-section">
          <div className="ini-section-heading">2. Set a Target PMPM</div>
          <div className="d-flex gap-3 align-items-end flex-wrap">
            <div>
              <label className="form-label ini-field-label">Target PMPM ($)</label>
              <input
                type="number"
                className="form-control ini-pmpm-input"
                value={targetPmpm}
                onChange={e => handleTargetChange(e.target.value)}
                disabled={!selectedPop}
                step="0.01"
              />
            </div>
            <div>
              <label className="form-label ini-field-label">Savings (%)</label>
              <input
                type="number"
                className="form-control ini-pmpm-input"
                value={savingsPct}
                onChange={e => handlePctChange(e.target.value)}
                disabled={!selectedPop}
                step="0.01"
              />
            </div>
            {initialPmpm && (
              <div className="ini-baseline-note">
                Baseline: <strong>{fmtDollar(initialPmpm)}</strong>
              </div>
            )}
          </div>
        </div>

        {/* Section 3: Dates */}
        <div className="ini-form-section">
          <div className="ini-section-heading">3. Select Dates</div>
          <div className="d-flex gap-3 flex-wrap">
            <div>
              <label className="form-label ini-field-label">Start Date</label>
              <input type="date" className="form-control ini-date-input" value={startDate} onChange={e => setStartDate(e.target.value)} />
            </div>
            <div>
              <label className="form-label ini-field-label">End Date</label>
              <input type="date" className="form-control ini-date-input" value={endDate} onChange={e => setEndDate(e.target.value)} />
            </div>
          </div>
        </div>

        {/* Section 4: Assignee */}
        <div className="ini-form-section">
          <div className="ini-section-heading">4. Set an Assignee</div>
          <div className="d-flex flex-column gap-2" style={{ maxWidth: 360 }}>
            <input
              type="text"
              className="form-control"
              placeholder="Assignee name"
              value={assignee}
              onChange={e => setAssignee(e.target.value)}
              disabled={enlaceManaged}
            />
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="enlace-managed"
                checked={enlaceManaged}
                onChange={e => handleEnlaceToggle(e.target.checked)}
              />
              <label className="form-check-label ini-field-label" htmlFor="enlace-managed">
                Request a quote for Enlace-managed initiative
              </label>
            </div>
          </div>
        </div>

        <div className="ini-form-actions">
          <button className="btn btn-secondary" onClick={onCancel}>Cancel</button>
          <button className="btn btn-primary" disabled={!canSave || saving} onClick={handleSave}>
            {saving ? 'Saving…' : 'Save Initiative'}
          </button>
        </div>
      </div>
    </div>
  );
}

function RecommendedTab({ onInitiativeSaved }) {
  const [categories, setCategories]     = useState(null);
  const [populations, setPopulations]   = useState(null);
  const [selectedInitiative, setSelectedInitiative] = useState(null);

  useEffect(() => {
    apiFetch('/api/initiatives/metrics').then(r => r.json()).then(setCategories).catch(() => setCategories([]));
    apiFetch('/api/initiatives/populations').then(r => r.json()).then(setPopulations).catch(() => setPopulations([]));
  }, []);

  if (selectedInitiative) {
    return (
      <CreationForm
        metric={selectedInitiative}
        populations={populations || []}
        onSave={() => { setSelectedInitiative(null); onInitiativeSaved(); }}
        onCancel={() => setSelectedInitiative(null)}
      />
    );
  }

  return <InitiativesTable categories={categories} onSelectInitiative={setSelectedInitiative} />;
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function InitiativesPage({ onNavigate, currentUser, onLogout }) {
  const [activeTab, setActiveTab] = useState(tabFromURL);

  useEffect(() => {
    const handler = () => setActiveTab(tabFromURL());
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, []);

  const switchTab = (key) => {
    history.pushState(null, '', '/initiatives?tab=' + key);
    setActiveTab(key);
  };

  return (
    <div className="d-flex flex-column" style={{ height: '100vh', background: 'var(--eh-navy)' }}>
      <nav className="navbar navbar-dark bg-primary px-4 py-2 flex-shrink-0">
        <span className="navbar-brand mb-0 d-flex align-items-center gap-3" style={{ cursor: 'pointer' }} onClick={() => onNavigate('home')}>
          <img src="/enlace-logo-white.svg" alt="Enlace Health" style={{ height: 36 }} />
          <span style={{ fontWeight: 300, opacity: 0.65, fontSize: '0.8rem', letterSpacing: '0.06em', textTransform: 'uppercase', color: '#fff' }}>
            Initiatives
          </span>
        </span>
        {currentUser && <AccountDropdown currentUser={currentUser} onLogout={onLogout} />}
      </nav>

      <div className="dash-tabs-bar flex-shrink-0">
        {TABS.map(t => (
          <button
            key={t.key}
            className={`dash-tab-btn${activeTab === t.key ? ' active' : ''}`}
            onClick={() => switchTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="flex-grow-1 overflow-auto" style={{ background: 'var(--eh-bg-page)' }}>
        <div className="dash-tab-content">
          {activeTab === 'tracked'     && <TrackedTab     onSwitchTab={switchTab} />}
          {activeTab === 'recommended' && <RecommendedTab onInitiativeSaved={() => switchTab('tracked')} />}
        </div>
      </div>
    </div>
  );
}
